/* ckc-sticky-cta.out.js — v1.1 (waits for the Launch fold)
 * Cape Kennedy Clothing — floating "Shop Collections" CTA.
 *
 * The primary hero button detaches as it scrolls off the main banner, then
 * rides down the viewport alongside the 3D hero objects and fades out well
 * before the FEATURED COLLECTIONS section arrives.
 *
 * Motion is frame-rate independent (exponential smoothing, same technique as
 * hero-3d.js v11) so it eases identically on 60Hz and 120Hz displays.
 * Honours prefers-reduced-motion. Homepage-only by virtue of requiring .hero-3d.
 */
(function () {
  'use strict';

  var SEL_BTN  = '.main-banner__button--1';
  var SEL_HERO = '.hero-3d';

  /* Travel band, as a fraction of viewport height. */
  var HANDOFF_D = 0.42, END_D = 0.74;   /* desktop */
  var HANDOFF_M = 0.38, END_M = 0.72;   /* mobile  */
  var FADE_START = 0.80;                /* hero progress where fade-out begins */
  var FADE_LEN   = 0.14;                /* ...and how long it takes  (gone @ .94) */
  var SMOOTH_K   = 9;                   /* higher = tighter follow */

  var inst = null;

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function StickyCTA() {
    this.btn  = document.querySelector(SEL_BTN);
    this.hero = document.querySelector(SEL_HERO);
    if (!this.btn || !this.hero || this.btn.hasAttribute('data-ckc-fcta-src')) return;

    this.reduced = !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    this.btn.setAttribute('data-ckc-fcta-src', '');
    this.section = this.hero.closest('.shopify-section') || this.hero;

    this.build();

    this.y       = 0;     /* smoothed viewport Y, px */
    this._w      = -1;    /* last applied width, px  */
    this.a       = 0;     /* smoothed alpha 0..1     */
    this.primed  = false;
    this.last    = 0;
    this.running = false;
    this.rafId   = null;

    this.tick   = this.tick.bind(this);
    this.wake   = this.wake.bind(this);

    window.addEventListener('scroll', this.wake, { passive: true });
    window.addEventListener('resize', this.wake, { passive: true });
    window.addEventListener('orientationchange', this.wake, { passive: true });

    this.wake();
  }

  StickyCTA.prototype.build = function () {
    var clone = this.btn.cloneNode(true);
    clone.removeAttribute('style');
    clone.removeAttribute('id');
    clone.removeAttribute('data-ckc-fcta-src');
    clone.classList.add('ckc-fcta__btn');
    clone.setAttribute('data-ckc-fcta-clone', '');

    var wrap = document.createElement('div');
    wrap.className = 'ckc-fcta';
    wrap.appendChild(clone);
    document.body.appendChild(wrap);

    this.wrap  = wrap;
    this.clone = clone;
    this.setInteractive(false);
  };

  /* Keep the floating copy out of the tab order / a11y tree while invisible,
     so it never becomes a phantom duplicate link. */
  StickyCTA.prototype.setInteractive = function (on) {
    if (this._interactive === on) return;
    this._interactive = on;
    this.wrap.classList.toggle('is-live', on);
    this.wrap.setAttribute('aria-hidden', on ? 'false' : 'true');
    if (on) this.clone.removeAttribute('tabindex');
    else this.clone.setAttribute('tabindex', '-1');
  };

  StickyCTA.prototype.wake = function () {
    if (this.running) return;
    this.running = true;
    this.last = 0;
    this.rafId = requestAnimationFrame(this.tick);
  };

  StickyCTA.prototype.tick = function (now) {
    var vh = window.innerHeight || 1;
    var mobile = window.innerWidth < 750;
    var handoff = (mobile ? HANDOFF_M : HANDOFF_D) * vh;
    var endY    = (mobile ? END_M    : END_D)    * vh;

    /* Keep the button clear of the notch / home indicator on phones. */
    var maxY = vh - 84 - (this.safeInset());
    if (endY > maxY) endY = maxY;
    if (endY < handoff) endY = handoff;

    var heroRect = this.hero.getBoundingClientRect();
    var span = Math.max(1, this.hero.offsetHeight - vh);
    var p = clamp(-heroRect.top / span, 0, 1);          /* hero scrub progress */

    var btnRect = this.btn.getBoundingClientRect();

    /* --- alpha ------------------------------------------------------ */
    /* In: as the original button climbs past the handoff line.          */
    var aIn = clamp((handoff - btnRect.top) / (vh * 0.10), 0, 1);
    /* Out: before the next section (FEATURED COLLECTIONS) can appear.   */
    var aOut = 1 - clamp((p - FADE_START) / FADE_LEN, 0, 1);

    /* Belt-and-braces: never linger once the next section nears view.  */
    var nextEl = this.section.nextElementSibling;
    if (nextEl) {
      var nTop = nextEl.getBoundingClientRect().top;
      if (nTop < vh * 1.15) {
        aOut = Math.min(aOut, clamp((nTop - vh * 0.85) / (vh * 0.30), 0, 1));
      }
    }

    var targetA = Math.min(aIn, aOut);

    /* CRO round two: the Launch fold (countdown card, product rail, badges)
       sits between the banner and the 3D hero. Keep the floating copy hidden
       until that section has cleared the handoff line, so it never covers a
       product card or the countdown. */
    var fold = document.querySelector('[data-ckc-fold]');
    if (fold) {
      var foldRect = fold.getBoundingClientRect();
      if (foldRect.bottom > handoff) targetA = 0;
    }

    /* --- position --------------------------------------------------- */
    var targetY;
    if (this.reduced) {
      targetY = (handoff + endY) / 2;
    } else {
      var q = clamp(p / FADE_START, 0, 1);
      targetY = handoff + (endY - handoff) * easeInOut(q);
    }

    /* --- smooth ----------------------------------------------------- */
    var dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : 0;
    this.last = now;

    if (!this.primed && targetA > 0) {
      /* First appearance: start exactly where the real button is, so the
         handoff reads as the button lifting off the page, not popping in. */
      this.y = btnRect.top;
      this.a = 0;
      this.primed = true;
    }

    var k = dt ? (1 - Math.exp(-SMOOTH_K * dt)) : 1;
    this.y += (targetY - this.y) * k;
    this.a += (targetA - this.a) * (dt ? (1 - Math.exp(-12 * dt)) : 1);

    if (targetA <= 0 && this.a < 0.004) { this.a = 0; this.primed = false; }

    /* Horizontal: lock to the real button's centre, and match its width.
       The banner content sits inside a max-width container, so viewport
       centring (left:50%) lands off to the side on desktop. */
    if (btnRect.width && Math.abs(btnRect.width - this._w) > 0.5) {
      this._w = btnRect.width;
      this.wrap.style.width = btnRect.width + 'px';
    }
    var cx = btnRect.left + btnRect.width / 2;
    this.wrap.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' +
      this.y.toFixed(1) + 'px,0) translateX(-50%)';
    this.wrap.style.opacity = this.a.toFixed(3);
    this.setInteractive(this.a > 0.06);

    /* Cross-fade the in-flow original so only one button is ever seen. */
    var oa = 1 - clamp(this.a * 1.7, 0, 1);
    this.btn.style.opacity = oa.toFixed(3);
    this.btn.style.pointerEvents = oa < 0.05 ? 'none' : '';

    /* Idle out when nothing is moving and the CTA is parked. */
    var settled = Math.abs(targetY - this.y) < 0.4 &&
                  Math.abs(targetA - this.a) < 0.004;
    if (settled && this.a === 0) {
      this.running = false;
      this.rafId = null;
      return;
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  StickyCTA.prototype.safeInset = function () {
    if (this._inset != null) return this._inset;
    var probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;bottom:0;height:env(safe-area-inset-bottom,0px);' +
      'width:0;visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);
    this._inset = probe.offsetHeight || 0;
    probe.remove();
    return this._inset;
  };

  StickyCTA.prototype.destroy = function () {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('scroll', this.wake);
    window.removeEventListener('resize', this.wake);
    window.removeEventListener('orientationchange', this.wake);
    if (this.wrap && this.wrap.parentNode) this.wrap.parentNode.removeChild(this.wrap);
    if (this.btn) {
      this.btn.style.opacity = '';
      this.btn.style.pointerEvents = '';
      this.btn.removeAttribute('data-ckc-fcta-src');
    }
  };

  function boot() {
    if (inst) { inst.destroy(); inst = null; }
    var probe = new StickyCTA();
    if (probe.wrap) inst = probe;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme editor: rebuild when the banner or hero section is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    if (e.target.querySelector(SEL_BTN) || e.target.querySelector(SEL_HERO)) boot();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (inst && e.target && e.target.querySelector(SEL_BTN)) { inst.destroy(); inst = null; }
  });
})();
